import { Component, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormField, form, maxLength, pattern, required } from '@angular/forms/signals';
import { ETipoDocumento } from '@core/enums/tipoDocumento.enum';
import { SunatApiService } from '@core/services/api/sunat-api.service';
import { IBuscarDocumentoReq } from '@core/interfaces/request-response/sunat.dto.interface';
import { IBuscarReniecRes } from '@core/interfaces/request-response/sunat.dto.interface';
import { ToastService } from '@shared/services/toast.service';
import { IBuscarSunatRes } from '@core/interfaces/request-response/sunat.dto.interface';
import { IClienteCrearReq } from '@core/interfaces/request-response/cliente.dto.interface';
import { ClienteApiService } from '@core/services/api/cliente-api.service';
import { IClienteData } from '@core/interfaces/entidades/cliente.entidad';
import { UiButton, UiInputMask, UiSelect, UiSelectButton } from '@shared/components/ui';
import { TipoDocumentoPipe } from '../../pipes/tipo-documento.pipe';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';

@Component({
  selector: 'app-crear-cliente',
  imports: [
    FormsModule,
    FormField,
    UiButton,
    UiInputMask,
    UiSelect,
    UiSelectButton,
    TipoDocumentoPipe,
    FormErrorMessagePipe,
  ],
  templateUrl: './crear-cliente.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './crear-cliente.css',
})
export class CrearCliente {
  private sunatApiService = inject(SunatApiService);
  private toastService = inject(ToastService);
  ETipoDocumento = ETipoDocumento;
  tipoBusqueda = [
    { label: 'DNI', value: ETipoDocumento.DNI },
    { label: 'RUC', value: ETipoDocumento.RUC },
    { label: 'CE', value: ETipoDocumento.CE },
    { label: 'PASAPORTE', value: ETipoDocumento.PASAPORTE },
    {
      label: 'P.T.P',
      value: ETipoDocumento.PERMISO_TEMPORAL_PERMANENCIA,
    },
    {
      label: 'Doc. Extranjero',
      value: ETipoDocumento.DOCUMENTO_IDENTIDAD_EXTRANJERO,
    },
  ];
  buscarSunat() {
    if (this.formularioCliente.dniRuc().invalid()) {
      this.toastService.show('top-right', 'warn', 'Advertencia', 'Ingrese un número de documento');
      return;
    }
    const req: IBuscarDocumentoReq = {
      numero_documento: this.formularioCliente.dniRuc().controlValue(),
      tipo_documento: this.formularioCliente.tipo_documento().controlValue(),
    };
    if (req.tipo_documento === ETipoDocumento.DNI) {
      this.sunatApiService.buscarDocumento<IBuscarReniecRes>(req).subscribe({
        next: (res) => {
          this.formularioCliente
            .nombre()
            .value.set(
              `${res.payload.nombres} ${res.payload.apellido_paterno} ${res.payload.apellido_materno}`,
            );
          this.formularioCliente.dniRuc().value.set(res.payload.dni);
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message || 'No se encontró el DNI en RENIEC',
          );
        },
      });
    } else if (req.tipo_documento === ETipoDocumento.RUC) {
      this.sunatApiService.buscarDocumento<IBuscarSunatRes>(req).subscribe({
        next: (res) => {
          this.formularioCliente.nombre().value.set(res.payload.razon_social);
          this.formularioCliente.dniRuc().value.set(res.payload.ruc);
          this.formularioCliente.direccion().value.set(res.payload.direccion_fiscal);
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message || 'No se encontró el DNI en RENIEC',
          );
        },
      });
    } else {
      this.toastService.show(
        'top-right',
        'warn',
        'Advertencia',
        'Solo se puede buscar por DNI o RUC. Por favor, cambie el tipo de documento a DNI o RUC para realizar la búsqueda.',
      );
    }
  }
  numero_celular?: string;
  formularioCliente = form(
    signal({
      nombre: '',
      dniRuc: '',
      direccion: '',
      tipo_documento: ETipoDocumento.DNI,
    }),
    (schemaPath) => {
      required(schemaPath.nombre, { message: 'El nombre es obligatorio' });
      required(schemaPath.dniRuc, { message: 'El DNI/RUC es obligatorio' });
      required(schemaPath.tipo_documento, { message: 'El tipo de documento es obligatorio' });

      pattern(schemaPath.nombre, /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ0-9\s.,'’&()\-/]+$/, {
        message:
          "El nombre solo puede contener letras, números, espacios y caracteres válidos (. , ' - & / ( )).",
      });
      maxLength(schemaPath.nombre, 125, {
        message: 'El nombre no puede exceder los 125 caracteres.',
      });
      maxLength(schemaPath.dniRuc, 30, {
        message: 'El DNI o RUC no puede exceder los 30 caracteres.',
      });
    },
  );
  crear = output<IClienteData>();

  private clienteApiService = inject(ClienteApiService);
  private checkInvalidForm(): boolean {
    if (this.formularioCliente().invalid()) {
      this.formularioCliente.nombre().markAsTouched();
      this.formularioCliente.dniRuc().markAsTouched();
      this.formularioCliente.direccion().markAsTouched();
      this.formularioCliente.tipo_documento().markAsTouched();
      return true;
    }
    return false;
  }
  crearCliente() {
    if (this.checkInvalidForm()) return;

    const cienteRQ: IClienteCrearReq = {
      nombre_completo: this.formularioCliente().controlValue().nombre,
      dniRuc: this.formularioCliente().controlValue().dniRuc,
      direccion: this.formularioCliente().controlValue().direccion,
      tipo_documento: this.formularioCliente().controlValue().tipo_documento,
      numero_celular: this.numero_celular,
    };
    this.clienteApiService.crear(cienteRQ).subscribe({
      next: (res) => {
        this.toastService.show('top-right', 'success', 'Éxito', 'Cliente creado correctamente');
        this.formularioCliente().reset();
        this.numero_celular = undefined;
        this.crear.emit(res);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al crear el cliente',
        );
      },
    });
  }
}
